import React, { useState } from 'react';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import './stripe.css';
import { ReactComponent as CvcIcon } from '../../icon/cvc.svg';
import { NotificationModal } from '../modal/notification-modal.component';

const CheckoutForm = ({ handleGetPaymentID }) => {
    const stripe = useStripe();
    const elements = useElements();

    // --- State Management ---
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('US');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    
    // States for handling submission, errors, and success messages
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [paymentMethodId, setPaymentMethodId] = useState(null);
    
    // States for notification modal
    const [showNotif, setShowNotif] = useState(false);
    const [titleNotif, setTitleNotif] = useState('');
    const [notifMessage, setNotifMessage] = useState('');
    
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!firstName || firstName.trim() === '' || !lastName || lastName.trim() === '') {
            setErrorMessage('First name and last name are required.');
            return;
        }

        if (!stripe || !elements) {
            return;
        }

        setIsConfirming(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        const cardNumberElement = elements.getElement(CardNumberElement);

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
            billing_details: {
                name: `${firstName} ${lastName}`,
                email: email,
                phone: phone,
                address: {
                    line1: address,
                    city: city,
                    state: state,
                    postal_code: postalCode,
                    country: country,
                },
            },
        });

        if (error) {
            setShowNotif(true);
            setTitleNotif("Error");
            setNotifMessage(error.message);
        } else {
            setPaymentMethodId(paymentMethod.id);
            setSuccessMessage(`Card details confirmed. Your payment method ID is ${paymentMethod.id}.`);
            if (handleGetPaymentID) {
                handleGetPaymentID(paymentMethod);
            }
        }
        setIsConfirming(false);
    };

    const handleNotification = () => {
        setShowNotif(false);
    };

    return (
        <form onSubmit={handleSubmit} className="checkout-form">
            {/* --- Section 1: Payment Information --- */}
            <div className="row">
                <div className="col-md-6 mb-3">
                    <label>First name</label>
                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required placeholder="Adam" className="form-control" />
                </div>
                <div className="col-md-6 mb-3">
                    <label>Last name</label>
                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required placeholder="Johnson" className="form-control" />
                </div>
            </div>
            <div className="mb-3">
                <label>Card number</label>
                <div className="card-number-wrapper">
                    <CardNumberElement className="stripe-element form-control" />
                    <div className="card-icons">
                        <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg" alt="Visa" />
                        <img src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg" alt="Mastercard" />
                        <img src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg" alt="Amex" />
                        <img src="https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg" alt="Discover" />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6 mb-2">
                    <label>Expiration date</label>
                    <CardExpiryElement className="stripe-element form-control" />
                </div>
                <div className="col-md-6 mb-2">
                    <label>CVV/CVC</label>
                    <div className="cvc-wrapper">
                        <CardCvcElement className="stripe-element form-control" />
                        <div className="cvc-icon"><CvcIcon /></div>
                    </div>
                </div>
            </div>

            {/* --- Section 2: Billing Address --- */}
            <hr className="mb-3" />
            <div className="row">
                <div className="col-md-6 mb-3">
                    <label>Email address</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="example@gmail.com" className="form-control" />
                </div>
                <div className="col-md-6 mb-3">
                    <label>Phone number</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+1 555-123-4567" className="form-control" />
                </div>
            </div>
            
            {/* --- MODIFIED SECTION --- */}
            <div className="row">
                {/* Address */}
                <div className="col-md-6 mb-3">
                    <label>Address</label>
                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} required placeholder="1234 Main St" className="form-control" />
                </div>
                {/* Country */}
                <div className="col-md-6 mb-3">
                    <label>Country</label>
                    <select value={country} onChange={(e) => setCountry(e.target.value)} required className="form-select">
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                    </select>
                </div>
            </div>
            
            <div className="row">
                {/* City */}
                <div className="col-md-4 mb-3">
                    <label>City</label>
                    <input type="text" value={city} onChange={(e) => setCity(e.target.value)} required placeholder="San Francisco" className="form-control" />
                </div>
                {/* State */}
                <div className="col-md-4 mb-3">
                    <label>State</label>
                    <input type="text" value={state} onChange={(e) => setState(e.target.value)} required placeholder="Texas" className="form-control" />
                </div>
                {/* Postal Code */}
                <div className="col-md-4 mb-3">
                    <label>Postal code</label>
                    <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required placeholder="73301" className="form-control" />
                </div>
            </div>
            {/* --- END OF MODIFIED SECTION --- */}
            
            {!paymentMethodId &&
                <button type="submit" disabled={!stripe || isConfirming} className="btn btn-primary w-50 d-block mx-auto mt-3">
                    {isConfirming ? 'Confirming...' : 'Save Card'}
                </button>
            }

            <div className="text-center mt-3 small text-muted">
                <span>Powered by <strong>stripe</strong></span>
                <span className="mx-2">|</span>
                <a href="#" className="text-muted text-decoration-none">Legal</a>
                <span className="mx-2">|</span>
                <a href="#" className="text-muted text-decoration-none">Returns</a>
                <span className="mx-2">|</span>
                <a href="#" className="text-muted text-decoration-none">Contact</a>
            </div>
           
            <NotificationModal
                show={showNotif}
                title={titleNotif}
                message={notifMessage}
                handleModal={handleNotification}
                customClass={'comment-notification-modal modal-text-bold notification-footer'}
            />
        </form>
    );
};

export default CheckoutForm;